import { useRef } from 'react';
import { InView } from 'react-intersection-observer';
import { Box, Typography } from '@mui/material';
import { Note } from '@models/note';
import { DialogNoteItem } from './DialogNoteItem';

interface NotesListProps {
  notes: Note[];
  searchBarText?: string; // optional text to highlight in notes
  fetchMore?: () => void; // callback to load next page
  isFetchingMore?: boolean; // optional loading state to suppress duplicate calls
  hasMore?: boolean; // indicates more pages available
  rootMargin?: string; // rootMargin for IntersectionObserver (e.g. '200px 0px')
  showMetadata?: boolean; // whether to fetch/show additional note metadata
}

export const NotesList = ({
  notes,
  searchBarText,
  fetchMore,
  isFetchingMore,
  hasMore = true,
  rootMargin = '200px 0px',
  showMetadata,
}: NotesListProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  return (
    <Box
      ref={containerRef}
      sx={{
        pt: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        px: 1,
        pb: 8,
        width: '100%',
      }}
    >
      {notes.map((n) =>
        n?.deletedByUser ? null : (
          <DialogNoteItem key={n.id} note={n} searchBarText={searchBarText} showMetadata={showMetadata} />
        ),
      )}
      {notes.length === 0 && (
        <Box sx={{ fontSize: 12, opacity: 0.6 }}>
          <Typography>{searchBarText ? 'No Results' : 'There are no notes for the selected view.'}</Typography>
        </Box>
      )}
      {fetchMore && hasMore && (
        <>
          <InView
            as='div'
            root={(containerRef.current as unknown as HTMLElement) || undefined}
            rootMargin={rootMargin}
            onChange={(inView) => {
              if (inView && fetchMore && !isFetchingMore) {
                fetchMore();
              }
            }}
            style={{ height: 1 }}
          />
          <Box sx={{ textAlign: 'center', mt: 1, fontSize: 11, opacity: 0.6 }}>
            <Typography variant='caption'>{isFetchingMore ? 'Loading more…' : ''}</Typography>
          </Box>
        </>
      )}
    </Box>
  );
};
